import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { toneClass, type Tone } from '../../../../components/Tone';
import { formatNumber } from '../../../../lib/format';
import styles from './BoardColumn.module.css';

/** While a card is carried: may it land here, and if not, why not. */
export type DropState = { readonly kind: 'idle' } | { readonly kind: 'ok'; readonly isOver: boolean; readonly hint: string | null } | { readonly kind: 'no'; readonly isOver: boolean; readonly reason: string };

interface BoardColumnProps {
  /** What a drop lands on: a pipeline stage (S16), or a review lane (R6's conveyor). */
  readonly lane: string;
  readonly tone: Tone;
  readonly label: string;
  readonly count: number;
  readonly shown: number;
  readonly isCollapsed: boolean;
  readonly onToggle: () => void;
  readonly drop: DropState;
  readonly empty: string;
  readonly footer?: ReactNode;
  /** Shares the board's width with the other lanes and grows with its cards instead of scrolling (④ Review, Idea B). */
  readonly isFluid?: boolean;
  readonly children?: ReactNode;
}

/**
 * One lane of SCR-03: a stage of the pipeline board, or a review lane of the conveyor (R6). Its count is the server's true count, not the cards it happens to
 * hold (Tomo's rule: a column showing 100 of 340 still says 340). Collapsed, it is a
 * strip that still takes a drop. During a drag it says whether the card may land here,
 * and why not when it may not — the same words the move would be refused with.
 */
export function BoardColumn({ lane, tone, label, count, shown, isCollapsed, onToggle, drop, empty, footer, isFluid = false, children }: BoardColumnProps): ReactNode {
  const dropClass = drop.kind === 'ok' ? (drop.isOver ? styles.dropOver : styles.dropOk) : drop.kind === 'no' ? styles.dropNo : '';

  if (isCollapsed) {
    return (
      <section className={[toneClass, styles.collapsed, dropClass].filter(Boolean).join(' ')} data-lane={lane} data-tone={tone} aria-label={`${label}, ${count}, collapsed`}>
        <button type="button" className={styles.expand} onClick={onToggle} aria-label={`Expand ${label}`}>
          <ChevronRight size={14} aria-hidden />
          <span className={styles.vertical}>
            {label} <strong>{formatNumber(count)}</strong>
          </span>
        </button>
      </section>
    );
  }

  return (
    <section className={[toneClass, styles.lane, isFluid ? styles.fluid : '', dropClass].filter(Boolean).join(' ')} data-lane={lane} data-tone={tone} aria-label={`${label}, ${count}`}>
      <header className={styles.head}>
        <h3 className={styles.title}>{label}</h3>
        <span className={styles.count}>{formatNumber(count)}</span>
        <button type="button" className={styles.collapse} onClick={onToggle} aria-label={`Collapse ${label}`}>
          <ChevronLeft size={14} aria-hidden />
        </button>
      </header>
      {drop.kind === 'no' && drop.isOver && <p className={styles.refusal}>{drop.reason}</p>}
      {drop.kind === 'ok' && drop.isOver && drop.hint && <p className={styles.hint}>{drop.hint}</p>}
      <div className={styles.body}>
        {children}
        {shown === 0 && <p className={styles.empty}>{empty}</p>}
      </div>
      {footer}
    </section>
  );
}
